import { FC, useMemo } from 'react';

import {
  GiftConversionBucket,
  GiftConversionResponse,
  GiftPacingCurvePoint,
  GiftPacingResponse,
} from '@shared/api/giftOrder/giftOrderDashboard.interface';

import { TD_CLASS, TH_CLASS } from '../../components/DashboardShared';
import { useGiftPacing } from './GiftConversionPacing';
import { fmt, fmtWonCompact, toKstDayKey } from './analysisUtils';

/** 이보다 진입자가 적은 구간은 비율이 튀어서 이상 판정에 쓰지 않는다 */
const MIN_SAMPLE = 20;
/** 직전 구간들의 중앙값 대비 이만큼 떨어지면 급락으로 본다 */
const DROP_THRESHOLD = 0.3;
/** 비교 기준으로 삼는 직전 구간 수 */
const LOOKBACK = 4;

const rate = (num: number, den: number): number | null => (den > 0 ? num / den : null);

const pctText = (v: number | null, digits = 1) =>
  v === null ? '—' : `${(v * 100).toFixed(digits)}%`;

const median = (values: number[]): number | null => {
  if (!values.length) return null;
  const s = [...values].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] ?? null : ((s[mid - 1] ?? 0) + (s[mid] ?? 0)) / 2;
};

interface Row {
  bucket: GiftConversionBucket;
  /** 답례글 진입율 (명) = 진입 상주 ÷ 전체 상주 */
  enterRate: number | null;
  /** 진입 기준 구매율 (명) = 진입 상주 중 구매한 수 ÷ 진입 상주 */
  buyRate: number | null;
  /** 표본이 적어 비율을 믿기 어려운 구간 */
  thin: boolean;
  /** 로그가 쌓이기 전 구간 — 0%가 실적이 아니라 데이터 없음 */
  beforeLog: boolean;
  /** 직전 구간들의 중앙값 (급락 판정 기준) */
  baseEnter: number | null;
  baseBuy: number | null;
  enterDrop: number | null;
  buyDrop: number | null;
}

/**
 * 답례글 퍼널 — "발인 상가의 상주가 답례글에 들어왔는가, 들어온 사람 중 몇 명이 샀는가".
 *
 * 여기만 상가(fevent)가 아니라 **명(상주 bugo)** 단위다. 답례글은 상주가 보는 페이지라
 * 상가 단위로 접으면 상주가 3명인 상가와 1명인 상가가 같은 1이 되어 진입율이 뭉개진다.
 *
 * 급락 판정은 직전 {LOOKBACK}구간의 중앙값 대비 -30% — 평균이 아니라 중앙값을 쓰는 건
 * 직전에 한 번 튄 구간이 기준을 통째로 흔들지 않게 하려는 것이다. 진입자 20명 미만
 * 구간은 비율이 튀므로 판정에서 뺀다(표기는 회색).
 */
export const GiftCourtesyFunnel: FC<{
  data: GiftConversionResponse;
  logStartKey: string | null;
  isBeforeLog: (b: GiftConversionBucket) => boolean;
  brandId: string;
  teamType: string;
}> = function GiftCourtesyFunnel({ data, logStartKey, isBeforeLog, brandId, teamType }) {
  const { totals, buckets } = data;
  // 기준 진입→구매율과 성숙 곡선 — 체인 기대치의 계수
  const { data: pacing } = useGiftPacing(brandId, teamType);

  const rows: Row[] = useMemo(() => {
    const list: Row[] = [];
    buckets.forEach((b, i) => {
      const enterRate = rate(b.courtesyViewedBugoCount, b.giftEnabledBugoCount);
      const buyRate = rate(b.courtesyViewedPurchasedBugoCount, b.courtesyViewedBugoCount);
      const beforeLog = isBeforeLog(b);
      const thin = b.courtesyViewedBugoCount < MIN_SAMPLE;

      // 직전 구간들 — 로그 없는 구간·표본 부족 구간은 기준에서 뺀다
      const prev = buckets
        .slice(Math.max(0, i - LOOKBACK), i)
        .filter((p) => !isBeforeLog(p) && p.courtesyViewedBugoCount >= MIN_SAMPLE);
      const baseEnter = median(
        prev
          .map((p) => rate(p.courtesyViewedBugoCount, p.giftEnabledBugoCount))
          .filter((v): v is number => v !== null),
      );
      const baseBuy = median(
        prev
          .map((p) => rate(p.courtesyViewedPurchasedBugoCount, p.courtesyViewedBugoCount))
          .filter((v): v is number => v !== null),
      );

      const dropOf = (cur: number | null, base: number | null) =>
        cur === null || base === null || base <= 0 ? null : (base - cur) / base;

      list.push({
        bucket: b,
        enterRate,
        buyRate,
        thin,
        beforeLog,
        baseEnter,
        baseBuy,
        enterDrop: beforeLog || thin ? null : dropOf(enterRate, baseEnter),
        buyDrop: beforeLog || thin ? null : dropOf(buyRate, baseBuy),
      });
    });
    return list;
  }, [buckets, isBeforeLog]);

  const alerts = rows.filter(
    (r) =>
      (r.enterDrop !== null && r.enterDrop >= DROP_THRESHOLD) ||
      (r.buyDrop !== null && r.buyDrop >= DROP_THRESHOLD),
  );

  const totalEnterRate = rate(
    totals.courtesyViewedBugoCount,
    totals.giftEnabledBugoCount,
  );
  const totalBuyRate = rate(
    totals.courtesyViewedPurchasedBugoCount,
    totals.courtesyViewedBugoCount,
  );
  const totalStoreBuyRate = rate(
    totals.storeViewedPurchasedBugoCount,
    totals.storeViewedBugoCount,
  );

  return (
    <div className="space-y-6">
      {/* ── 한 줄 퍼널 (명 기준) ── */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-sm font-semibold text-gray-800">
          답례글 퍼널 — 명(상주) 기준
        </h3>
        <p className="mt-0.5 mb-4 text-xs text-gray-400">
          상가가 아니라 상주 한 명이 1이다 (상주가 3명인 상가와 1명인 상가를 같게 세면
          진입율이 뭉개진다) · 분모는 <b>답례품 기능이 켜진 상가의 상주</b>만 — 기능이
          꺼진 상가는 답례글에서 스토어 자체가 안 뜨고 로그도 안 찍혀서, 섞으면 신규
          상조가 들어올 때마다 진입율이 떨어져 보인다
          {logStartKey && ` · 조회 로그는 ${logStartKey}부터`}
        </p>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatBox
            label="대상 상주"
            value={`${fmt(totals.giftEnabledBugoCount)}명`}
            sub={`답례품 기능 ON 상가 ${fmt(
              totals.giftEnabledFeventCount,
            )}곳 / 전체 ${fmt(totals.feventCount)}곳`}
          />
          <StatBox
            label="답례글 진입"
            value={`${fmt(totals.courtesyViewedBugoCount)}명`}
            sub={`진입율 ${pctText(totalEnterRate)}`}
            accent="indigo"
          />
          <StatBox
            label="진입 → 구매"
            value={pctText(totalBuyRate)}
            sub={`${fmt(totals.courtesyViewedPurchasedBugoCount)}명 / 진입 ${fmt(
              totals.courtesyViewedBugoCount,
            )}명`}
            accent="emerald"
          />
          <StatBox
            label="스토어 진입 → 구매"
            value={pctText(totalStoreBuyRate)}
            sub={`${fmt(totals.storeViewedPurchasedBugoCount)}명 / 진입 ${fmt(
              totals.storeViewedBugoCount,
            )}명 (참고)`}
          />
        </div>
        <p className="mt-3 text-xs text-gray-400">
          구매 상주 {fmt(totals.purchasedBugoCount)}명 중 답례글을 거친 사람이{' '}
          {fmt(totals.courtesyViewedPurchasedBugoCount)}명 — 나머지는 답례글 로그 없이
          (부고/직접 링크로) 산 사람이다.
        </p>
      </div>

      {/* ── 체인 기대치 ── */}
      <ChainExpectation
        rows={rows}
        pacing={pacing}
        completeBufferDays={data.completeBufferDays}
      />

      {/* ── 급락 감지 ── */}
      <div
        className={`rounded-lg p-6 shadow ${
          alerts.length ? 'border border-red-200 bg-red-50' : 'bg-white'
        }`}
      >
        <h3 className="text-sm font-semibold text-gray-800">
          급락 감지
          {alerts.length > 0 && (
            <span className="ml-2 rounded-full bg-red-600 px-2 py-0.5 text-xs text-white">
              {alerts.length}건
            </span>
          )}
        </h3>
        <p className="mt-0.5 text-xs text-gray-400">
          직전 {LOOKBACK}구간 중앙값 대비 30% 이상 하락 · 진입자 {MIN_SAMPLE}명 미만
          구간은 비율이 튀어서 판정에서 뺀다
        </p>
        {alerts.length === 0 ? (
          <p className="mt-3 text-sm text-gray-500">급락으로 볼 만한 구간이 없습니다.</p>
        ) : (
          <ul className="mt-3 space-y-2">
            {alerts.map((r) => (
              <li key={r.bucket.key} className="text-sm text-gray-800">
                <span className="font-semibold">{r.bucket.key}</span>
                {r.enterDrop !== null && r.enterDrop >= DROP_THRESHOLD && (
                  <span className="ml-2">
                    답례글 진입율 {pctText(r.enterRate)}{' '}
                    <span className="text-red-600">
                      (기준 {pctText(r.baseEnter)} 대비 −{(r.enterDrop * 100).toFixed(0)}
                      %)
                    </span>
                  </span>
                )}
                {r.buyDrop !== null && r.buyDrop >= DROP_THRESHOLD && (
                  <span className="ml-2">
                    진입→구매율 {pctText(r.buyRate)}{' '}
                    <span className="text-red-600">
                      (기준 {pctText(r.baseBuy)} 대비 −{(r.buyDrop * 100).toFixed(0)}%)
                    </span>
                  </span>
                )}
                <span className="ml-2 text-xs text-gray-500">
                  진입 {fmt(r.bucket.courtesyViewedBugoCount)}명 / 대상 상주{' '}
                  {fmt(r.bucket.giftEnabledBugoCount)}명
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* ── 추이 ── */}
      <div className="rounded-lg bg-white p-6 shadow">
        <h3 className="text-sm font-semibold text-gray-800">진입율 · 진입→구매율 추이</h3>
        <p className="mt-0.5 mb-4 text-xs text-gray-400">
          막대 = 답례글 진입 상주 수 · 파란 선 = 진입율 · 초록 선 = 진입한 사람 중 구매율
          · 빗금 구간은 로그가 없거나 표본이 적어 판단하지 않는다
        </p>
        <RateChart rows={rows} />
      </div>

      {/* ── 표 ── */}
      <div className="overflow-hidden rounded-lg bg-white shadow">
        <div className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold text-gray-800">구간별 상세 (명)</h3>
        </div>
        <div className="max-h-[520px] overflow-x-auto overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr>
                <th className={TH_CLASS}>구간</th>
                <th className={`${TH_CLASS} text-right`}>대상 상주</th>
                <th className={`${TH_CLASS} text-right`}>답례글 진입</th>
                <th className={`${TH_CLASS} text-right`}>진입율</th>
                <th className={`${TH_CLASS} text-right`}>진입 중 구매</th>
                <th className={`${TH_CLASS} text-right`}>진입→구매율</th>
                <th className={`${TH_CLASS} text-right`}>기준 대비</th>
                <th className={`${TH_CLASS} text-right`}>구매 상주(전체)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => {
                const alert =
                  (r.buyDrop !== null && r.buyDrop >= DROP_THRESHOLD) ||
                  (r.enterDrop !== null && r.enterDrop >= DROP_THRESHOLD);
                return (
                  <tr
                    key={r.bucket.key}
                    className={alert ? 'bg-red-50' : 'hover:bg-gray-50'}
                  >
                    <td className={`${TD_CLASS} font-medium`}>
                      {r.bucket.key}
                      {r.beforeLog && (
                        <span className="ml-1 text-[10px] text-gray-400">로그 없음</span>
                      )}
                      {!r.beforeLog && r.thin && (
                        <span className="ml-1 text-[10px] text-gray-400">표본 적음</span>
                      )}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {fmt(r.bucket.giftEnabledBugoCount)}
                      {r.bucket.giftEnabledBugoCount !== r.bucket.bugoCount && (
                        <span className="ml-1 text-[10px] text-gray-400">
                          /전체 {fmt(r.bucket.bugoCount)}
                        </span>
                      )}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {r.beforeLog ? '—' : fmt(r.bucket.courtesyViewedBugoCount)}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {r.beforeLog ? '—' : pctText(r.enterRate)}
                    </td>
                    <td className={`${TD_CLASS} text-right`}>
                      {r.beforeLog ? '—' : fmt(r.bucket.courtesyViewedPurchasedBugoCount)}
                    </td>
                    <td
                      className={`${TD_CLASS} text-right font-semibold ${
                        r.buyDrop !== null && r.buyDrop >= DROP_THRESHOLD
                          ? 'text-red-600'
                          : ''
                      }`}
                    >
                      {r.beforeLog ? '—' : pctText(r.buyRate)}
                    </td>
                    <td className={`${TD_CLASS} text-right text-xs text-gray-500`}>
                      {r.buyDrop === null
                        ? '—'
                        : `${r.buyDrop > 0 ? '−' : '+'}${Math.abs(
                            r.buyDrop * 100,
                          ).toFixed(0)}%`}
                    </td>
                    <td className={`${TD_CLASS} text-right text-gray-500`}>
                      {fmt(r.bucket.purchasedBugoCount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── 체인 기대치 ─────────────────────────────────────

/** 곡선에서 경과일 d의 누적 비중 (선형 보간) */
const cumAt = (curve: GiftPacingCurvePoint[], day: number): number => {
  const first = curve[0];
  const last = curve[curve.length - 1];
  if (!first || !last) return 1;
  if (day <= first.day) return first.cumShare;
  if (day >= last.day) return last.cumShare;
  for (let i = 1; i < curve.length; i++) {
    const prev = curve[i - 1];
    const cur = curve[i];
    if (!prev || !cur) continue;
    if (day <= cur.day) {
      const span = cur.day - prev.day;
      const r = span === 0 ? 0 : (day - prev.day) / span;
      return prev.cumShare + (cur.cumShare - prev.cumShare) * r;
    }
  }
  return last.cumShare;
};

/**
 * 진입자 수 → 기준 구매율 → 성숙도 보정 → 기대 구매자·건수·매출.
 *
 * 사용자가 보고 싶어 한 사슬 그대로다: "답례글에 N명이 들어왔고, 들어온 사람은 보통
 * X% 사고, 이 구간은 아직 Y%밖에 안 익었으니, 지금쯤 Z명이 샀어야 한다".
 *
 * 성숙도는 구간 중앙값 경과일로 근사한다 — 구간 안의 발인일이 고르게 퍼져 있다는
 * 가정이라, 지난 구간(다 익은 구간)에서는 어차피 1이 되고 현재 구간에서만 의미가 있다.
 */
const ChainExpectation: FC<{
  rows: Row[];
  pacing: GiftPacingResponse | null;
  completeBufferDays: number;
}> = ({ rows, pacing, completeBufferDays }) => {
  const base = pacing?.baseline ?? null;

  const chain = useMemo(() => {
    if (!base || base.courtesyViewedBugoCount <= 0) return null;
    const now = Date.now();
    const acc = {
      viewed: 0,
      expBuyer: 0,
      expOrder: 0,
      expAmount: 0,
      actBuyer: 0,
      actOrder: 0,
      actAmount: 0,
      perBucket: [] as {
        key: string;
        viewed: number;
        progress: number;
        expBuyer: number;
        actBuyer: number;
      }[],
    };
    for (const r of rows) {
      if (r.beforeLog) continue;
      const b = r.bucket;
      const viewed = b.courtesyViewedBugoCount;
      if (viewed <= 0) continue;
      // 구간 중앙 발인일 기준 경과일
      const mid =
        (new Date(toKstDayKey(b.from)).getTime() +
          new Date(toKstDayKey(b.to)).getTime()) /
        2;
      const elapsed = (now - mid) / (24 * 60 * 60 * 1000);
      const pFirst = cumAt(base.firstPurchaseCurve, elapsed);
      const pOrder = cumAt(base.orderCurve, elapsed);

      const expBuyer = viewed * base.buyRatePerViewedBugo * pFirst;
      acc.viewed += viewed;
      acc.expBuyer += expBuyer;
      acc.expOrder += viewed * base.ordersPerViewedBugo * pOrder;
      acc.expAmount += viewed * base.amountPerViewedBugo * pOrder;
      acc.actBuyer += b.courtesyViewedPurchasedBugoCount;
      acc.actOrder += b.orderCount;
      acc.actAmount += b.amount;
      acc.perBucket.push({
        key: b.key,
        viewed,
        progress: pFirst,
        expBuyer,
        actBuyer: b.courtesyViewedPurchasedBugoCount,
      });
    }
    return acc;
  }, [rows, base]);

  if (!base) {
    return (
      <div className="rounded-lg bg-white p-6 text-sm text-gray-500 shadow">
        <h3 className="mb-1 text-sm font-semibold text-gray-800">체인 기대치</h3>
        기준 표본이 부족해 "진입한 사람 치고 보통 얼마나 사는지"를 만들 수 없습니다.
      </div>
    );
  }
  if (!chain || chain.viewed === 0) {
    return (
      <div className="rounded-lg bg-white p-6 text-sm text-gray-500 shadow">
        <h3 className="mb-1 text-sm font-semibold text-gray-800">체인 기대치</h3>이 구간에
        답례글 진입 로그가 없습니다.
      </div>
    );
  }

  const tone = (r: number) =>
    r < 0.8 ? 'text-red-600' : r > 1.1 ? 'text-emerald-600' : 'text-gray-800';
  const ratio = (a: number, e: number) => (e > 0 ? a / e : 0);

  const cards = [
    {
      label: '구매자 (명)',
      expected: `${chain.expBuyer.toFixed(1)}명`,
      actual: `${fmt(chain.actBuyer)}명`,
      r: ratio(chain.actBuyer, chain.expBuyer),
      note: `진입 ${fmt(chain.viewed)}명 × 기준 구매율 ${(
        base.buyRatePerViewedBugo * 100
      ).toFixed(1)}%`,
    },
    {
      label: '주문 건수',
      expected: `${chain.expOrder.toFixed(1)}건`,
      actual: `${fmt(chain.actOrder)}건`,
      r: ratio(chain.actOrder, chain.expOrder),
      note: `진입자당 ${base.ordersPerViewedBugo}건`,
    },
    {
      label: '매출',
      expected: `${fmtWonCompact(Math.round(chain.expAmount))}원`,
      actual: `${fmtWonCompact(chain.actAmount)}원`,
      r: ratio(chain.actAmount, chain.expAmount),
      note: `진입자당 ${fmtWonCompact(base.amountPerViewedBugo)}원`,
    },
  ];

  return (
    <div className="rounded-lg bg-white p-6 shadow">
      <h3 className="text-sm font-semibold text-gray-800">
        체인 기대치 — 진입자 기준으로 적당한 숫자가 팔렸나
      </h3>
      <p className="mt-0.5 mb-4 text-xs text-gray-400">
        답례글 진입 {fmt(chain.viewed)}명 × 기준 구매율{' '}
        {(base.buyRatePerViewedBugo * 100).toFixed(1)}% × 성숙도(발인 후 경과일 곡선) =
        기대치. 기준 구간 {toKstDayKey(base.from)} ~ {toKstDayKey(base.to)}의 진입자{' '}
        {fmt(base.courtesyViewedBugoCount)}명에서 학습했고, 발인 +{completeBufferDays}
        일이 안 지난 구간은 진행률만큼 기대치를 깎는다
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {cards.map((c) => (
          <div key={c.label} className="rounded-lg bg-gray-50 px-4 py-3">
            <p className="text-xs text-gray-500">{c.label}</p>
            <p className={`mt-1 text-xl font-bold ${tone(c.r)}`}>
              {Math.round(c.r * 100)}%
            </p>
            <p className="mt-0.5 text-xs text-gray-500">
              기대 {c.expected} → 실제{' '}
              <span className="font-semibold text-gray-700">{c.actual}</span>
            </p>
            <p className="mt-0.5 text-[11px] text-gray-400">{c.note}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full text-xs">
          <thead>
            <tr className="text-gray-400">
              <th className="py-1 text-left font-medium">구간</th>
              <th className="py-1 text-right font-medium">진입</th>
              <th className="py-1 text-right font-medium">성숙도</th>
              <th className="py-1 text-right font-medium">기대 구매자</th>
              <th className="py-1 text-right font-medium">실제</th>
              <th className="py-1 text-right font-medium">달성률</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {chain.perBucket.map((b) => {
              const r = ratio(b.actBuyer, b.expBuyer);
              return (
                <tr key={b.key} className="border-t border-gray-100">
                  <td className="py-1">{b.key}</td>
                  <td className="py-1 text-right">{fmt(b.viewed)}명</td>
                  <td className="py-1 text-right">{(b.progress * 100).toFixed(0)}%</td>
                  <td className="py-1 text-right">{b.expBuyer.toFixed(1)}명</td>
                  <td className="py-1 text-right">{fmt(b.actBuyer)}명</td>
                  <td className={`py-1 text-right font-semibold ${tone(r)}`}>
                    {b.expBuyer >= 1 ? `${Math.round(r * 100)}%` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-gray-400">
        주문·매출의 "실제"는 그 구간 전체 실적이다 — 진입 로그 없이 산 사람도 포함되니
        구매자 달성률보다 후하게 나온다. 판단은 구매자(명) 축을 먼저 본다.
      </p>
    </div>
  );
};

// ─── 내부 컴포넌트 ───────────────────────────────────

const StatBox: FC<{
  label: string;
  value: string;
  sub?: string;
  accent?: 'indigo' | 'emerald';
}> = ({ label, value, sub, accent }) => (
  <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
    <p className="text-xs text-gray-500">{label}</p>
    <p
      className={`mt-1 text-xl font-bold ${
        accent === 'indigo'
          ? 'text-indigo-700'
          : accent === 'emerald'
          ? 'text-emerald-600'
          : 'text-gray-900'
      }`}
    >
      {value}
    </p>
    {sub && <p className="mt-0.5 text-xs text-gray-400">{sub}</p>}
  </div>
);

const CHART_H = 200;
const PAD_L = 40;
const PAD_B = 22;

/** 진입자 수 막대 + 두 비율 선 (좌 축은 비율 0~max, 막대는 자체 스케일) */
const RateChart: FC<{ rows: Row[] }> = ({ rows }) => {
  const maxRate = Math.max(
    0.05,
    ...rows.flatMap((r) => [r.enterRate ?? 0, r.buyRate ?? 0]),
  );
  const maxBar = Math.max(1, ...rows.map((r) => r.bucket.courtesyViewedBugoCount));
  const n = rows.length || 1;
  const step = (1000 - PAD_L - 8) / n;
  const cx = (i: number) => PAD_L + step * (i + 0.5);
  const cy = (v: number) => (1 - v / maxRate) * (CHART_H - PAD_B);

  const linePath = (pick: (r: Row) => number | null) => {
    let d = '';
    let started = false;
    rows.forEach((r, i) => {
      const v = r.beforeLog || r.thin ? null : pick(r);
      if (v === null) {
        started = false;
        return;
      }
      d += `${started ? 'L' : 'M'}${cx(i)},${cy(v)}`;
      started = true;
    });
    return d;
  };

  return (
    <div className="overflow-x-auto">
      <svg
        viewBox={`0 0 1000 ${CHART_H}`}
        className="h-52 w-full min-w-[560px]"
        preserveAspectRatio="none"
      >
        {[0, 0.25, 0.5, 0.75, 1].map((g) => (
          <g key={g}>
            <line
              x1={PAD_L}
              x2={992}
              y1={cy(maxRate * g)}
              y2={cy(maxRate * g)}
              stroke="#f1f5f9"
            />
            <text x={0} y={cy(maxRate * g) + 4} fontSize={11} fill="#94a3b8">
              {(maxRate * g * 100).toFixed(0)}%
            </text>
          </g>
        ))}
        {/* 진입자 수 막대 */}
        {rows.map((r, i) => {
          const h = (r.bucket.courtesyViewedBugoCount / maxBar) * (CHART_H - PAD_B) * 0.5;
          return (
            <rect
              key={r.bucket.key}
              x={cx(i) - step * 0.3}
              y={CHART_H - PAD_B - h}
              width={step * 0.6}
              height={Math.max(h, 0)}
              fill={r.beforeLog || r.thin ? '#e5e7eb' : '#c7d2fe'}
            />
          );
        })}
        <path
          d={linePath((r) => r.enterRate)}
          fill="none"
          stroke="#6366f1"
          strokeWidth={2.5}
        />
        <path
          d={linePath((r) => r.buyRate)}
          fill="none"
          stroke="#10b981"
          strokeWidth={2.5}
        />
        {/* 급락 지점 표시 */}
        {rows.map((r, i) =>
          r.buyDrop !== null && r.buyDrop >= DROP_THRESHOLD && r.buyRate !== null ? (
            <circle
              key={`a${r.bucket.key}`}
              cx={cx(i)}
              cy={cy(r.buyRate)}
              r={4}
              fill="#dc2626"
            />
          ) : null,
        )}
        {rows.map((r, i) =>
          i % Math.ceil(n / 12) === 0 ? (
            <text
              key={`x${r.bucket.key}`}
              x={cx(i)}
              y={CHART_H - 5}
              fontSize={10}
              fill="#94a3b8"
              textAnchor="middle"
            >
              {r.bucket.key.slice(2)}
            </text>
          ) : null,
        )}
      </svg>
      <div className="mt-1 flex gap-4 text-xs text-gray-500">
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-4 bg-indigo-500" /> 답례글 진입율
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-0.5 w-4 bg-emerald-500" /> 진입→구매율
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-indigo-200" /> 진입 상주
          수
        </span>
      </div>
    </div>
  );
};
